/**
 * LcViewToggle.js — Calendar view switcher (Month / Week / Day / Agenda).
 *
 * Single source of truth for the segmented pill toggle shown above the
 * react-big-calendar on the Search, All Programs and My Events pages.
 *
 * Each pill is a thin proxy: clicking it triggers a click on the matching
 * hidden react-big-calendar view button (rendered by CalendarToolbar), which
 * performs the actual view change. This component owns only the pill UI and
 * the active-state styling — behaviour is otherwise unchanged from the inline
 * markup it replaces.
 *
 * A single yellow indicator bar slides to the active pill (transform + width
 * transition) as a visual cue, instead of the colour jumping between pills.
 *
 * Props:
 *  - id    {string}                      Container id (kept unique per page
 *                                        so the hidden proxy ids never clash).
 *  - items {Array<{label, id, view}>}    Pills in order. `id` is the hidden
 *                                        proxy button to click for the actual
 *                                        view change; `view` is the matching
 *                                        react-big-calendar view name
 *                                        ("month" | "week" | "day" | "agenda").
 *  - view  {string}                      The calendar's current active view
 *                                        (from react-big-calendar's onView).
 *                                        Drives which pill renders active, so
 *                                        the toggle stays in sync even when
 *                                        the view changes via drill-down
 *                                        (e.g. clicking a date in Week view)
 *                                        rather than a pill click.
 */

import React, { useCallback, useLayoutEffect, useEffect, useRef } from 'react';

export const LcViewToggle = ({ id, items, view }) => {
  const activeIndex = Math.max(0, items.findIndex((item) => item.view === view));
  const listRef = useRef(null);
  const indicatorRef = useRef(null);
  const animatedOnce = useRef(false);

  /**
   * Slide the yellow indicator bar horizontally to sit under the active pill
   * and match its width. Height / vertical inset are owned by CSS so the bar
   * never changes the toggle's size. Reads live geometry so it stays correct
   * across breakpoints / full-width (flex:1) layouts. The first positioning
   * skips the transition so the bar appears in place rather than sliding in
   * from the edge.
   */
  const positionIndicator = useCallback(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;
    const activeBtn = list.querySelectorAll('.lc-toggle-btn')[activeIndex];
    if (!activeBtn) return;

    if (!animatedOnce.current) indicator.style.transition = 'none';
    indicator.style.width = activeBtn.offsetWidth + 'px';
    indicator.style.transform = 'translateX(' + activeBtn.offsetLeft + 'px)';

    if (!animatedOnce.current) {
      // Force a reflow so the no-transition placement commits, then re-enable
      // transitions for every subsequent move.
      void indicator.offsetWidth;
      indicator.style.transition = '';
      animatedOnce.current = true;
    }
  }, [activeIndex]);

  useLayoutEffect(() => {
    positionIndicator();
  }, [positionIndicator]);

  useEffect(() => {
    window.addEventListener('resize', positionIndicator);
    return () => window.removeEventListener('resize', positionIndicator);
  }, [positionIndicator]);

  return (
    <div className="lc-cal-view-toggle" id={id} ref={listRef}>
      <span className="lc-toggle-indicator" aria-hidden="true" ref={indicatorRef} />
      {items.map((item, idx) => (
        <button
          key={item.id}
          type="button"
          className={idx === activeIndex ? 'lc-toggle-btn lc-toggle-active' : 'lc-toggle-btn'}
          onClick={() => document.getElementById(item.id)?.click()}
        >{item.label}</button>
      ))}
    </div>
  );
};

export default LcViewToggle;
